import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const CryptoWatchlist: React.FC = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(contentRef, { once: false, amount: 0.2 });
    const [selectedAlarm, setSelectedAlarm] = useState<number>(1); // Default selected alarm

    // Mock data for watchlist coins - reduced to 3
    const watchlistCoins = [
        { id: 1, name: 'Bitcoin', symbol: 'BTC', price: 32450.78, change: 2.4, volume: 28.5, favorite: true, chart: generateMockChartData(30, 0.8), color: 'orange-500' },
        { id: 2, name: 'Ethereum', symbol: 'ETH', price: 1820.35, change: 3.8, volume: 15.2, favorite: true, chart: generateMockChartData(30, 0.6), color: 'blue-500' },
        { id: 3, name: 'Solana', symbol: 'SOL', price: 76.52, change: 5.2, volume: 7.8, favorite: true, chart: generateMockChartData(30, 0.9), color: 'purple-500' },
    ];

    // Function to generate mock chart data
    function generateMockChartData(points: number, trend: number) {
        const data = [];
        let value = 100;

        for (let i = 0; i < points; i++) {
            // Add some random noise to the trend
            const noise = (Math.random() - 0.5) * 10;
            value = value + (trend * 3) + noise;
            value = Math.max(value, 10); // Ensure the value stays above 10
            data.push(value);
        }

        return data;
    }

    // Animation variants for cards
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3,
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: [0.25, 0.25, 0, 1]
            }
        }
    };

    // Calculate if a coin's chart is uptrend or downtrend
    const getChartTrend = (chartData: number[]) => {
        return chartData[chartData.length - 1] > chartData[0];
    };

    // Animation variants for alarm popover
    const alarmVariants = {
        hidden: { opacity: 0, y: 10, scale: 0.95 },
        visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
        exit: { opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.2 } }
    };

    // Auto-show the alarm for the selected coin on page load
    useEffect(() => {
        const timer = setTimeout(() => {
            setSelectedAlarm(1);
        }, 1500);

        return () => clearTimeout(timer);
    }, []);

    return (
        <section
            ref={sectionRef}
            id="watchlist"
            className="section bg-primary py-16 overflow-hidden"
        >
            {/* Background patterns for visual interest */}
            <div className="absolute inset-0 opacity-5 overflow-hidden">
                <div className="absolute top-0 right-0 w-1/2 h-1/2 rounded-full bg-accent/10 blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 rounded-full bg-accent/10 blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>

                {/* Grid pattern */}
                <div className="h-full w-full grid grid-cols-6 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="border-r border-white/5 h-full"></div>
                    ))}
                </div>
            </div>

            <div className="container-custom relative z-10" ref={contentRef}>
                <div className="flex flex-col md:flex-row items-start justify-between gap-12">
                    <motion.div
                        className="w-full md:w-5/12 md:sticky md:top-32"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="mb-6 inline-block px-3 py-1 bg-accent/10 rounded-full text-accent text-sm font-medium">
                            Crypto Watchlist
                        </div>
                        <h2 className="heading-lg mb-6">Track Your <span className="text-accent">Favorite</span> Cryptocurrencies</h2>
                        <p className="text-gray-400 mb-8">
                            Keep an eye on market movements with customizable alerts and real-time data. Add any cryptocurrency to your watchlist and never miss an opportunity.
                        </p>

                        <div className="space-y-6 mb-8">
                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Real-time Monitoring</h3>
                                    <p className="text-gray-400 text-sm">Track price movements, volume, and market cap in real-time.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Custom Alerts</h3>
                                    <p className="text-gray-400 text-sm">Set price alerts and get notified when your conditions are met.</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="flex items-start gap-3"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <div className="mt-1 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-medium">Technical Indicators</h3>
                                    <p className="text-gray-400 text-sm">Access key technical indicators to inform your trading decisions.</p>
                                </div>
                            </motion.div>
                        </div>

                        <motion.button
                            className="btn-primary mt-4"
                            whileHover={{
                                scale: 1.05,
                                boxShadow: '0 0 15px rgba(109, 40, 217, 0.5)'
                            }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Create Watchlist
                        </motion.button>
                    </motion.div>

                    <div className="w-full md:w-7/12">
                        {/* Watchlist Cards */}
                        <motion.div
                            className="space-y-4"
                            variants={containerVariants}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                        >
                            {/* Table header */}
                            <div className="hidden md:grid grid-cols-12 gap-4 text-gray-400 text-sm py-2 px-4">
                                <div className="col-span-4">Asset</div>
                                <div className="col-span-2 text-right">Price</div>
                                <div className="col-span-2 text-right">24h</div>
                                <div className="col-span-3">Chart (7d)</div>
                                <div className="col-span-1 text-right">Alert</div>
                            </div>

                            {/* Coin Items */}
                            {watchlistCoins.map((coin) => (
                                <div key={coin.id} className="relative">
                                    <motion.div
                                        className="card border border-white/5 hover:border-accent/20 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                                        variants={itemVariants}
                                        whileHover={{
                                            scale: 1.01,
                                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)'
                                        }}
                                        whileTap={{ scale: 0.99 }}
                                    >
                                        {/* Mobile view: Asset with price info */}
                                        <div className="md:hidden flex justify-between items-center">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full bg-${coin.color}/20 flex items-center justify-center`}>
                                                    <span className={`font-mono font-bold text-${coin.color}`}>{coin.symbol}</span>
                                                </div>
                                                <div>
                                                    <div className="font-medium">{coin.name}</div>
                                                    <div className="text-sm text-gray-400">${coin.price.toLocaleString()}</div>
                                                </div>
                                            </div>
                                            <div className={`text-sm ${coin.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {coin.change >= 0 ? '+' : ''}{coin.change}%
                                            </div>
                                        </div>

                                        {/* Desktop view: Separate columns */}
                                        <div className="hidden md:flex md:col-span-4 items-center gap-3">
                                            <motion.div
                                                className={`w-10 h-10 rounded-full bg-${coin.color}/20 flex items-center justify-center`}
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                            >
                                                <span className={`font-mono font-bold text-${coin.color}`}>{coin.symbol}</span>
                                            </motion.div>
                                            <div>
                                                <div className="font-medium">{coin.name}</div>
                                                <div className="text-xs text-gray-400">Vol: ${coin.volume}B</div>
                                            </div>
                                        </div>

                                        <div className="hidden md:block md:col-span-2 text-right font-mono font-medium">
                                            ${coin.price.toLocaleString()}
                                        </div>

                                        <div className="hidden md:block md:col-span-2 text-right">
                                            <motion.span
                                                className={`text-sm ${coin.change >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                                animate={{
                                                    opacity: [0.8, 1, 0.8]
                                                }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                {coin.change >= 0 ? '+' : ''}{coin.change}%
                                            </motion.span>
                                        </div>

                                        {/* Mini chart */}
                                        <div className="md:col-span-3 h-10 relative">
                                            <svg viewBox="0 0 100 30" className="w-full h-full">
                                                <defs>
                                                    <linearGradient id={`gradient-${coin.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                                        <stop
                                                            offset="0%"
                                                            stopColor={getChartTrend(coin.chart) ? "#10B981" : "#EF4444"}
                                                            stopOpacity="0.2"
                                                        />
                                                        <stop
                                                            offset="100%"
                                                            stopColor={getChartTrend(coin.chart) ? "#10B981" : "#EF4444"}
                                                            stopOpacity="0"
                                                        />
                                                    </linearGradient>
                                                </defs>
                                                {/* Line chart */}
                                                <motion.path
                                                    d={`M 0 ${30 - (coin.chart[0] / 200) * 30} ${coin.chart.map((point, i) =>
                                                        ` L ${(i / (coin.chart.length - 1)) * 100} ${30 - (point / 200) * 30}`
                                                    ).join('')}`}
                                                    fill="none"
                                                    stroke={getChartTrend(coin.chart) ? "#10B981" : "#EF4444"}
                                                    strokeWidth="1"
                                                    initial={{ pathLength: 0 }}
                                                    animate={{ pathLength: 1 }}
                                                    transition={{ duration: 1.5, delay: 0.3 }}
                                                />
                                                {/* Area under chart */}
                                                <motion.path
                                                    d={`M 0 ${30 - (coin.chart[0] / 200) * 30} ${coin.chart.map((point, i) =>
                                                        ` L ${(i / (coin.chart.length - 1)) * 100} ${30 - (point / 200) * 30}`
                                                    ).join('')} L 100 30 L 0 30 Z`}
                                                    fill={`url(#gradient-${coin.id})`}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 1, delay: 0.8 }}
                                                />
                                            </svg>
                                        </div>

                                        {/* Alert button with popover */}
                                        <div className="md:col-span-1 flex justify-end">
                                            <motion.div
                                                className="relative"
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                            >
                                                <motion.button
                                                    className={`w-8 h-8 rounded-full ${selectedAlarm === coin.id ? 'bg-accent text-white' : 'bg-secondary/80 text-gray-400'} flex items-center justify-center`}
                                                    whileHover={{
                                                        scale: 1.2,
                                                        backgroundColor: 'rgba(0, 112, 243, 0.2)',
                                                        color: '#0070F3',
                                                    }}
                                                    whileTap={{ scale: 0.9 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedAlarm(selectedAlarm === coin.id ? 0 : coin.id);
                                                    }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                                    </svg>
                                                </motion.button>
                                            </motion.div>
                                        </div>
                                    </motion.div>

                                    {/* Alert Popover Box */}
                                    <AnimatePresence>
                                        {selectedAlarm === coin.id && (
                                            <motion.div
                                                variants={alarmVariants}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                                className="absolute right-0 top-16 z-10 w-64 p-3 bg-white rounded-lg shadow-lg border border-accent/10"
                                            >
                                                <div className="flex items-start mb-2">
                                                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent mr-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h5 className="text-sm font-medium text-gray-800">Price Alert</h5>
                                                        <p className="text-xs text-gray-500">{coin.name} {coin.change >= 0 ? 'up' : 'down'} {Math.abs(coin.change)}%</p>
                                                    </div>
                                                    <button
                                                        className="ml-auto w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedAlarm(0);
                                                        }}
                                                    >
                                                        <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div className="pl-2 border-l-2 border-accent">
                                                    <p className="text-xs text-gray-600">
                                                        {coin.symbol === 'BTC' && "Bitcoin has passed your price target. Consider taking partial profits or setting a new target."}
                                                        {coin.symbol === 'ETH' && "Ethereum price movement breaking key resistance. Potential for continued upward momentum."}
                                                        {coin.symbol === 'SOL' && "Solana volume spike detected. Unusual activity might indicate upcoming volatility."}
                                                    </p>
                                                </div>
                                                <div className="mt-2 flex justify-end gap-2">
                                                    <motion.button
                                                        className="px-2 py-1 text-xs border border-gray-200 rounded-md text-gray-700"
                                                        whileHover={{ scale: 1.05, backgroundColor: "#f9fafb" }}
                                                        whileTap={{ scale: 0.95 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedAlarm(0);
                                                        }}
                                                    >
                                                        Dismiss
                                                    </motion.button>
                                                    <motion.button
                                                        className="px-2 py-1 text-xs bg-accent text-white rounded-md"
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        View Details
                                                    </motion.button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CryptoWatchlist; 